import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { UserService } from '../_services/user.service';
import { LaunchDarklyService } from '../../services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import {
  BulkUpdateUsers,
  BulkUpdateUsersFailed,
  BulkUpdateUsersSuccess,
  LoadAllPlans,
  LoadAllPlansFailed,
  LoadAllPlansSuccess,
  LoadApplicationRoles,
  LoadApplicationRolesFailed,
  LoadApplicationRolesSuccess,
  LoadCurrentPlan,
  LoadCurrentPlanFailed,
  LoadCurrentPlanSuccess,
  LoadDefaultPaymentMethod,
  LoadDefaultPaymentMethodFailed,
  LoadDefaultPaymentMethodSuccess,
  LoadStripePortal,
  LoadStripePortalFailed,
  LoadStripePortalSuccess,
  LoadUsers,
  LoadUsersFailed,
  LoadUsersSuccess,
  Profile,
  ProfileFailed,
  ProfileSuccess,
  SubscribePlan,
  SubscribePlanFailed,
  SubscribePlanSuccess,
  ToggleEmailAlerts,
  ToggleEmailAlertsFailed,
  ToggleEmailAlertsSuccess,
  UpdateActive,
  UpdateActiveFailed,
  UpdateActiveSuccess,
  UpdateProfile,
  UpdateProfileFailed,
  UpdateProfileSuccess,
  UpdateUserEmailFailed,
  UpdateUserEmailSuccess,
  UserActionTypes,
  ValidateUserEmailFailed,
  ValidateUserEmailSuccess,
} from '../_actions/user.actions';
import { Role, UserProfile } from '../_models/user.models';
import {
  getApplicationRoles,
  getDefaultPaymentMethod,
  getUsers,
} from '../_selectors/user.selector';
import { CacheService } from '../../services/cache.service';
import { AvailableCaches } from '../_models/ignatius.models';
import { BulkUpdateClientSuccess } from '../_actions/clients.actions';

@Injectable()
export class UserEffect {
  constructor(
    private actions: Actions,
    private service: UserService,
    private store: Store,
    private readonly cache: CacheService,
    private readonly ldService: LaunchDarklyService
  ) {}

  actionServiceMap:any = {
    [UserActionTypes.VALIDATE_USER_EMAIL]: {
      serviceMethod: this.service.validateUserEmail,
      successAction: ValidateUserEmailSuccess,
      failureAction: ValidateUserEmailFailed,
    },
    [UserActionTypes.UPDATE_USER_EMAIL]: {
      serviceMethod: this.service.updateUserEmail,
      successAction: UpdateUserEmailSuccess,
      failureAction: UpdateUserEmailFailed,
    },
  };

  /**
   * Get profile
   * Only passes applicationKey to service when feature flag is enabled
   */
  profile: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<Profile>(UserActionTypes.PROFILE),
      mergeMap(async (action) => {
        // Check feature flag before passing applicationKey to service
        const isEntityLevelRoleEnabled = await this.ldService
          .getFlagValue(FEATURE_FLAGS.ENABLE_ENTITY_LEVEL_TOP_LEVEL_ROLE, false)
          .pipe(take(1))
          .toPromise();
        
        // Only pass applicationKey if feature flag is enabled
        const applicationKey = isEntityLevelRoleEnabled ? action.applicationKey : undefined;
        
        return this.service.getProfile(applicationKey).pipe(
          map((payload: UserProfile) => new ProfileSuccess(payload, applicationKey)),
          catchError((err) => of(new ProfileFailed(err)))
        );
      }),
      mergeMap(obs => obs)
    )
  );

  /**
   * Update profile
   */
  updateProfile: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdateProfile>(UserActionTypes.UPDATE_PROFILE),
      mergeMap((action) => {
        return this.service.updateProfile(action.payload).pipe(
          map((payload: UserProfile) => {
            const instance = new UpdateProfileSuccess(<any>action.payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdateProfileFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Update active
   */
  updateActive: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdateActive>(UserActionTypes.UPDATE_ACTIVE),
      mergeMap((action) => {
        return this.service.updateActive(action.payload).pipe(
          map((payload: UserProfile) => {
            const instance = new UpdateActiveSuccess(<any>action.payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdateActiveFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Toggle Email Alerts
   */
  toggleEmailAlerts: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<ToggleEmailAlerts>(UserActionTypes.TOGGLE_EMAIL_ALERTS),
      mergeMap((action) => {
        return this.service.toggleEmailAlerts(action.payload).pipe(
          map((payload: UserProfile) => {
            const instance = new ToggleEmailAlertsSuccess(<any>action.payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new ToggleEmailAlertsFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * load application roles
   */
  applicationRoles: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplicationRoles>(UserActionTypes.LOAD_APPLICATION_ROLES),
      withLatestFrom(this.store.pipe(select(getApplicationRoles))),
      mergeMap(([action, roles]) => {
        if (
          roles[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.APPLICATION_USERS) &&
          !action.force
        )
          return of(
            new LoadApplicationRolesSuccess(
              action.applicationId,
              roles[action.applicationId]
            )
          );
        return this.service.applicationRoles(action.applicationId,action.tableId,action.fromForm).pipe(
          map(
            (payload: Array<Role>) =>
              new LoadApplicationRolesSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadApplicationRolesFailed(err, action.applicationId)))
        );
      })
    )
  );

  /**
   * load users
   */
  loadUsers: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadUsers>(UserActionTypes.LOAD_USERS),
      withLatestFrom(this.store.pipe(select(getUsers))),
      mergeMap(([action, users]) => {
        if (
          users[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.APPLICATION_USERS) &&
          !action.force
        )
          return of(
            new LoadUsersSuccess(
              action.applicationId,
              users[action.applicationId]
            )
          );
        return this.service
          .getUsers(
            action.applicationId,
            action.isSearch,
            action.search,
            action.isScroll,
            action.skip,
            action.pageSize,
            action.isOptimizedComponent
          )
          .pipe(
            map(
              (payload: Array<UserProfile>) =>
                new LoadUsersSuccess(action.applicationId, payload)
            ),
            catchError((err) => of(new LoadUsersFailed(err)))
          );
      })
    )
  );

  /**
   * Get default payment method
   */
  getDefaultPaymentMethod: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadDefaultPaymentMethod>(
        UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD
      ),
      withLatestFrom(this.store.pipe(select(getDefaultPaymentMethod))),
      mergeMap(([action]) =>
        this.service.getDefaultPaymentMethod(action.customerId).pipe(
          map((payload: any) => new LoadDefaultPaymentMethodSuccess(payload)),
          catchError((err) => of(new LoadDefaultPaymentMethodFailed(err)))
        )
      )
    )
  );

  /**
   * Get current plan
   */
  currentPlan: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadCurrentPlan>(UserActionTypes.LOAD_CURRENT_PLAN),
      mergeMap(() =>
        this.service.getCurrentPlan().pipe(
          map((payload: any) => new LoadCurrentPlanSuccess(payload)),
          catchError((err) => of(new LoadCurrentPlanFailed(err)))
        )
      )
    )
  );

  /**
   * Get all plans
   */
  allPlans: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadAllPlans>(UserActionTypes.LOAD_ALL_PLANS),
      mergeMap(() =>
        this.service.getAllPlans().pipe(
          map((payload: any) => new LoadAllPlansSuccess(payload)),
          catchError((err) => of(new LoadAllPlansFailed(err)))
        )
      )
    )
  );

  /**
   * Get stripe portal
   */
  stripePortal: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadStripePortal>(UserActionTypes.LOAD_STRIPE_PORTAL),
      mergeMap(() =>
        this.service.getStripeCustomerPortal().pipe(
          map((payload: any) => new LoadStripePortalSuccess(payload)),
          catchError((err) => of(new LoadStripePortalFailed(err)))
        )
      )
    )
  );

  /**
   * Subscribe Plan
   */
  subscribePlan: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<SubscribePlan>(UserActionTypes.SUBSCRIBE_PLAN),
      mergeMap(() =>
        this.service.subscribePlan().pipe(
          map((payload: any) => new SubscribePlanSuccess(payload)),
          catchError((err) => of(new SubscribePlanFailed(err)))
        )
      )
    )
  );

  /**
   * Effects for Email Updation
   */
  updateUserEffect: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(
        UserActionTypes.VALIDATE_USER_EMAIL,
        UserActionTypes.UPDATE_USER_EMAIL
      ),
      map((action:any) => {
        const { serviceMethod, successAction, failureAction } = this.actionServiceMap[action?.type];

        serviceMethod.call(this.service, action.payload).subscribe(
          (payload:any) => {
            const instance = new successAction(payload);
            instance.refId = action.refId;
            this.store.dispatch(instance);
          },
          (err:any) => this.store.dispatch(new failureAction(err))
        );
        return { type: '[Update] No Operation' };
      })
    )
  );

  bulkUpdateUsers: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<BulkUpdateUsers>(UserActionTypes.BULK_UPDATE_USERS),
      switchMap((action) => {
        return this.service.bulkUpdateUsers(action.payload).pipe(
          map((payload: any) => {
            const instance = new BulkUpdateUsersSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new BulkUpdateUsersFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );
}
